/**
 * Refine: research runs again, optionally with a refinement, what the
 * requester wants it to reconsider or know (markdown, often a reply to the
 * report). The refinement is kept on the new run and every later run's prompt
 * reads it; earlier ones are shown only in History, one per run. The
 * submission itself is never edited.
 */

import { useState } from 'react';
import { WandSparkles } from 'lucide-react';

import { RerunIn, rerunInNoteOneMax } from '@/api/generated/schemas';
import { useRerunResearch } from '@/api/queries';
import type { IdeaDetail } from '@/api/types';
import { bannerError, fieldErrors, serverFieldErrors } from '@/api/validate';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { CharCount } from '@/ui/CharCount';
import { MarkdownEditor } from '@/ui/MarkdownEditor';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';

export function RefineDialog({ idea, onClose, onQueued }: { idea: IdeaDetail; onClose: () => void; onQueued: () => void }) {
  const rerun = useRerunResearch();
  const [note, setNote] = useState('');
  const invalid = Object.keys(fieldErrors(RerunIn, { note })).length > 0;
  const server = serverFieldErrors(rerun.error);

  function onSubmit() {
    if (rerun.isPending || invalid) return;
    rerun.mutate({ id: idea.id, note: note.trim() || undefined }, { onSuccess: onQueued });
  }

  return (
    <Modal
      title="Refine"
      subtitle="Research runs again, with anything you add on what to reconsider or what it did not know. The submission stays as it was."
      size="wide"
      onClose={() => {
        rerun.reset();
        onClose();
      }}
      onSubmit={onSubmit}
      footer={
        <div className="submit-actions">
          <span className="hint">Uses AI budget. The current scores stay until the new run finishes; every run is kept.</span>
          <span className="row">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <ActionButton
              state={buttonState(rerun)}
              icon={<WandSparkles aria-hidden="true" />}
              label="Refine"
              pendingLabel="Queuing…"
              doneLabel="Queued"
              disabled={invalid}
            />
          </span>
        </div>
      }
    >
      <div className="stack-md">
        <FormError error={bannerError(rerun.error, ['note'])} />
        <div className="field">
          <label htmlFor="refine-note">Refinement (optional)</label>
          <MarkdownEditor
            id="refine-note"
            required={false}
            rows={7}
            maxLength={rerunInNoteOneMax}
            value={note}
            placeholder="What should research reconsider? For example a market it missed, a client GFT already serves, or a score you disagree with and why. Markdown is supported."
            describedBy="refine-note-count"
            error={server.note}
            onChange={setNote}
          />
          <CharCount id="refine-note-count" value={note} max={rerunInNoteOneMax}>
            Research weighs it on its merits; it does not set scores.
          </CharCount>
        </div>
      </div>
    </Modal>
  );
}
